// GerenciarMateriais.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, doc, deleteDoc, query, where, updateDoc } from 'firebase/firestore';
import { auth, db, storage } from '../firebaseConfig.js';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import {
  FaPlus,
  FaArrowLeft,
  FaArrowUp,
  FaTimes,
  FaEye,
  FaSearch,
  FaTrash,
  FaEdit,
  FaSave,
  FaFilePdf,
  FaImage
} from 'react-icons/fa';
import './GerenciarMateriais.css';
import { normalizeText } from '../utils/text';

/** ========= Utilitários pdf.js ========= **/
async function ensurePdfJsLoaded() {
  if (window.pdfjsLib?.getDocument) return;
  await new Promise((resolve, reject) => {
    // evita inserir múltiplas vezes
    const existing = document.querySelector('script[data-pdfjs]');
    if (existing) {
      existing.onload = resolve;
      existing.onerror = reject;
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.async = true;
    s.setAttribute('data-pdfjs', '1');
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  // seta worker (precisa ser depois do script principal)
  if (!window.pdfjsLib?.GlobalWorkerOptions?.workerSrc) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }
}

async function renderPdfFirstPageToCanvas({ source, targetCanvas, maxWidth = 1400 }) {
  await ensurePdfJsLoaded();

  let loadingTask;
  if (source instanceof File) {
    const buf = await source.arrayBuffer();
    loadingTask = window.pdfjsLib.getDocument({ data: buf });
  } else if (source instanceof ArrayBuffer) {
    loadingTask = window.pdfjsLib.getDocument({ data: source });
  } else {
    loadingTask = window.pdfjsLib.getDocument({ url: source });
  }

  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);

  const unscaled = page.getViewport({ scale: 1 });
  const scale = Math.min(maxWidth / unscaled.width, 2.0);
  const viewport = page.getViewport({ scale });

  targetCanvas.width = Math.floor(viewport.width);
  targetCanvas.height = Math.floor(viewport.height);

  const ctx = targetCanvas.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, targetCanvas.width, targetCanvas.height);

  await page.render({ canvasContext: ctx, viewport }).promise;
  return { width: targetCanvas.width, height: targetCanvas.height };
}

function blobFromCanvas(canvas, type = 'image/jpeg', quality = 0.9) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** ========= Modal de Recorte (com loader, retry e fallback) ========= **/
function PdfCropperModal({ file, onConfirm, onCancel }) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const [renderSize, setRenderSize] = useState({ w: 800, h: 450 }); // fallback inicial
  const [displaySize, setDisplaySize] = useState({ w: 800, h: 450 });
  const [dragging, setDragging] = useState(false);
  const [crop, setCrop] = useState(null); // {x,y,w,h} em coords do canvas
  const [lockRatio, setLockRatio] = useState(true); // 16:9 travado por padrão

  const [isRendering, setIsRendering] = useState(true);
  const [error, setError] = useState('');

  const tempPreviewUrl = useRef('');
  const [previewUrl, setPreviewUrl] = useState('');

  async function doRender() {
    setError('');
    setIsRendering(true);
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;
      await renderPdfFirstPageToCanvas({ source: file, targetCanvas: canvas, maxWidth: 1600 });

      // Ajusta dimensões exibidas (responsivo)
      const maxDisplay = Math.min(window.innerWidth - 80, 1000);
      const scale = Math.min(1, maxDisplay / canvas.width);
      const dw = Math.max(320, Math.floor(canvas.width * scale));
      const dh = Math.max(180, Math.floor(canvas.height * scale));

      setRenderSize({ w: canvas.width, h: canvas.height });
      setDisplaySize({ w: dw, h: dh });

      // Crop inicial central 16:9
      const w = Math.floor(canvas.width * 0.8);
      const h = lockRatio ? Math.floor((w * 9) / 16) : Math.floor(canvas.height * 0.5);
      const x = Math.floor((canvas.width - w) / 2);
      const y = Math.floor((canvas.height - h) / 3);
      setCrop({ x, y, w, h });
    } catch (e) {
      console.error('Erro ao renderizar PDF:', e);
      setError('Não foi possível carregar a primeira página do PDF. Tente novamente.');
    } finally {
      setIsRendering(false);
    }
  }

  useEffect(() => {
    doRender();
    return () => {
      if (tempPreviewUrl.current) {
        URL.revokeObjectURL(tempPreviewUrl.current);
        tempPreviewUrl.current = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // redesenha overlay
  useEffect(() => {
    const overlay = overlayRef.current;
    const canvas = canvasRef.current;
    if (!overlay || !canvas || !crop) return;

    overlay.width = displaySize.w;
    overlay.height = displaySize.h;

    const ctx = overlay.getContext('2d');
    ctx.clearRect(0, 0, overlay.width, overlay.height);

    // Escala display->render
    const scale = displaySize.w / renderSize.w;

    // Sombreado
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fillRect(0, 0, overlay.width, overlay.height);

    const rx = Math.floor(crop.x * scale);
    const ry = Math.floor(crop.y * scale);
    const rw = Math.floor(crop.w * scale);
    const rh = Math.floor(crop.h * scale);

    // "furo"
    ctx.clearRect(rx, ry, rw, rh);

    // Borda
    ctx.strokeStyle = '#00bcd4';
    ctx.lineWidth = 2;
    ctx.strokeRect(rx, ry, rw, rh);

    // Alças simples
    const handle = 10;
    ctx.fillStyle = '#00bcd4';
    ctx.fillRect(rx - handle, ry - handle, handle * 2, handle * 2);
    ctx.fillRect(rx + rw - handle, ry - handle, handle * 2, handle * 2);
    ctx.fillRect(rx - handle, ry + rh - handle, handle * 2, handle * 2);
    ctx.fillRect(rx + rw - handle, ry + rh - handle, handle * 2, handle * 2);
  }, [crop, displaySize, renderSize]);

  const posToRenderCoords = (evt) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = evt.clientX - rect.left;
    const cy = evt.clientY - rect.top;
    // escala display->render
    const scale = renderSize.w / displaySize.w;
    return {
      x: Math.max(0, Math.min(renderSize.w, Math.round(cx * scale))),
      y: Math.max(0, Math.min(renderSize.h, Math.round(cy * scale)))
    };
  };

  const dragState = useRef({ mode: null, startX: 0, startY: 0, startCrop: null });

  const hitHandle = (x, y, c) => {
    const s = 14;
    if (Math.abs(x - c.x) <= s && Math.abs(y - c.y) <= s) return 'tl';
    if (Math.abs(x - (c.x + c.w)) <= s && Math.abs(y - c.y) <= s) return 'tr';
    if (Math.abs(x - c.x) <= s && Math.abs(y - (c.y + c.h)) <= s) return 'bl';
    if (Math.abs(x - (c.x + c.w)) <= s && Math.abs(y - (c.y + c.h)) <= s) return 'br';
    if (x >= c.x && x <= c.x + c.w && y >= c.y && y <= c.y + c.h) return 'move';
    return null;
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    if (isRendering || error) return;
    const p = posToRenderCoords(e);
    const mode = crop ? hitHandle(p.x, p.y, crop) : null;

    if (!mode) {
      dragState.current = { mode: 'new', startX: p.x, startY: p.y, startCrop: null };
      setDragging(true);
      setCrop({ x: p.x, y: p.y, w: 1, h: 1 });
      return;
    }

    dragState.current = { mode, startX: p.x, startY: p.y, startCrop: { ...crop } };
    setDragging(true);
  };

  const onMouseMove = (e) => {
    if (!dragging) return;
    const { mode, startX, startY, startCrop } = dragState.current;
    const p = posToRenderCoords(e);

    if (mode === 'new') {
      let x = Math.min(startX, p.x);
      let y = Math.min(startY, p.y);
      let w = Math.abs(p.x - startX);
      let h = Math.abs(p.y - startY);

      if (lockRatio) {
        const targetH = Math.round((w * 9) / 16);
        if (targetH > h) h = targetH;
        else w = Math.round((h * 16) / 9);
        if (x + w > renderSize.w) w = renderSize.w - x;
        if (y + h > renderSize.h) h = renderSize.h - y;
      }
      setCrop({ x, y, w, h });
      return;
    }

    if (!startCrop) return;
    let { x, y, w, h } = startCrop;

    if (mode === 'move') {
      const dx = p.x - startX;
      const dy = p.y - startY;
      x = Math.max(0, Math.min(renderSize.w - w, x + dx));
      y = Math.max(0, Math.min(renderSize.h - h, y + dy));
      setCrop({ x, y, w, h });
      return;
    }

    const left = x;
    const right = x + w;
    const top = y;
    const bottom = y + h;

    let nx = left, ny = top, nr = right, nb = bottom;

    if (mode === 'tl') { nx = Math.min(p.x, right - 10); ny = Math.min(p.y, bottom - 10); }
    if (mode === 'tr') { nr = Math.max(p.x, left + 10); ny = Math.min(p.y, bottom - 10); }
    if (mode === 'bl') { nx = Math.min(p.x, right - 10); nb = Math.max(p.y, top + 10); }
    if (mode === 'br') { nr = Math.max(p.x, left + 10); nb = Math.max(p.y, top + 10); }

    if (lockRatio) {
      const ww = nr - nx;
      const hh = nb - ny;
      const targetH = Math.round((ww * 9) / 16);
      if (targetH > hh) {
        const extra = targetH - hh;
        if (mode === 'tl' || mode === 'tr') ny = Math.max(0, ny - extra);
        else nb = Math.min(renderSize.h, nb + extra);
      } else {
        const targetW = Math.round((hh * 16) / 9);
        if (mode === 'tl' || mode === 'bl') nx = Math.max(0, nr - targetW);
        else nr = Math.min(renderSize.w, nx + targetW);
      }
    }

    nx = Math.max(0, Math.min(renderSize.w - 10, nx));
    ny = Math.max(0, Math.min(renderSize.h - 10, ny));
    nr = Math.max(nx + 10, Math.min(renderSize.w, nr));
    nb = Math.max(ny + 10, Math.min(renderSize.h, nb));

    setCrop({ x: nx, y: ny, w: nr - nx, h: nb - ny });
  };

  const onMouseUp = () => setDragging(false);
  const onMouseLeave = () => setDragging(false);

  // Preview do recorte (mini)
  useEffect(() => {
    const genPreview = async () => {
      if (!crop || isRendering || error) return;
      const base = canvasRef.current;
      if (!base) return;

      const off = document.createElement('canvas');
      off.width = Math.max(1, Math.floor(crop.w));
      off.height = Math.max(1, Math.floor(crop.h));
      const ctx = off.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, off.width, off.height);
      ctx.drawImage(
        base,
        Math.floor(crop.x),
        Math.floor(crop.y),
        Math.floor(crop.w),
        Math.floor(crop.h),
        0, 0, off.width, off.height
      );
      const blob = await blobFromCanvas(off, 'image/jpeg', 0.9);
      if (tempPreviewUrl.current) {
        URL.revokeObjectURL(tempPreviewUrl.current);
        tempPreviewUrl.current = '';
      }
      const url = URL.createObjectURL(blob);
      tempPreviewUrl.current = url;
      setPreviewUrl(url);
    };
    genPreview();
  }, [crop, isRendering, error]);

  const confirmCrop = async () => {
    if (!crop || isRendering || error) return;
    const base = canvasRef.current;
    const out = document.createElement('canvas');
    // exporto em 800x450 quando lock 16:9; senão no tamanho proporcional (mín 800 largura)
    if (lockRatio) {
      out.width = 800;
      out.height = 450;
    } else {
      out.width = Math.max(800, Math.floor(crop.w));
      out.height = Math.floor((out.width * crop.h) / crop.w);
    }
    const ctx = out.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(
      base,
      Math.floor(crop.x),
      Math.floor(crop.y),
      Math.floor(crop.w),
      Math.floor(crop.h),
      0, 0, out.width, out.height
    );
    const blob = await blobFromCanvas(out, 'image/jpeg', 0.9);
    onConfirm(blob);
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div className="modal-content" style={{ maxWidth: '1100px', width: '90%' }}>
        <button className="modal-close" onClick={onCancel} aria-label="Fechar">
          <FaTimes />
        </button>
        <h2 style={{ marginBottom: '0.5rem' }}>Ajustar preview do PDF</h2>
        <p style={{ color: '#6c757d', marginTop: 0, marginBottom: '1rem' }}>
          Arraste o retângulo para definir a área. {lockRatio ? 'Proporção 16:9 travada.' : 'Proporção livre.'}
        </p>

        {/* Área do canvas */}
        <div style={{ position: 'relative', width: displaySize.w, margin: '0 auto' }}>
          {/* Canvas base (PDF) */}
          <canvas
            ref={canvasRef}
            style={{
              width: displaySize.w + 'px',
              height: displaySize.h + 'px',
              display: isRendering ? 'none' : 'block',
              background: '#fff'
            }}
          />
          {/* Overlay de recorte */}
          <canvas
            ref={overlayRef}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: displaySize.w + 'px',
              height: displaySize.h + 'px',
              cursor: dragging ? 'grabbing' : 'crosshair',
              display: isRendering || error ? 'none' : 'block'
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
          />

          {/* Loader */}
          {isRendering && !error && (
            <div style={{
              width: displaySize.w,
              height: displaySize.h,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: 8
            }}>
              Carregando primeira página do PDF…
            </div>
          )}

          {/* Erro + Retry */}
          {error && (
            <div style={{
              width: displaySize.w,
              minHeight: 200,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff3f3',
              border: '1px solid #f5c6cb',
              borderRadius: 8,
              color: '#842029',
              padding: '1rem',
              textAlign: 'center'
            }}>
              {error}
              <div>
                <button className="ver-conteudo" onClick={doRender} style={{ marginRight: 8 }}>
                  Recarregar
                </button>
                <button className="fechar-button" onClick={onCancel}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        {!error && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={lockRatio}
                onChange={(e) => setLockRatio(e.target.checked)}
                disabled={isRendering}
              />
              Travar em 16:9
            </label>

            {previewUrl && !isRendering && (
              <div style={{ marginLeft: 'auto' }}>
                <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '0.25rem' }}>Pré-visualização:</div>
                <img
                  src={previewUrl}
                  alt="Pré-visualização do recorte"
                  style={{ width: 240, height: lockRatio ? 135 : 'auto', objectFit: 'cover', borderRadius: 8, border: '1px solid #e9ecef' }}
                />
              </div>
            )}
          </div>
        )}

        <div className="modal-actions" style={{ marginTop: '1rem' }}>
          <button onClick={confirmCrop} className="ver-conteudo" disabled={isRendering || !!error}>
            Confirmar recorte
          </button>
          <button onClick={onCancel} className="fechar-button">
            <FaTimes /> Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

const FILE_MATERIAL_TYPES = new Set(['PDF', 'PNG', 'JPG', 'DOCUMENTO', 'SLIDE']);

function isFileMaterialType(type) {
  return FILE_MATERIAL_TYPES.has((type || '').toUpperCase());
}

function getFormMaterialType(materialType) {
  const normalized = (materialType || '').toUpperCase();

  if (normalized === 'DOC' || normalized === 'DOCX') return 'Documento';
  if (normalized === 'PPT' || normalized === 'PPTX') return 'Slide';
  if (normalized === 'JPEG' || normalized === 'JPG') return 'JPG';
  if (normalized === 'PNG') return 'PNG';
  if (normalized === 'PDF') return 'PDF';
  if (normalized === 'YOUTUBE') return 'YouTube';
  if (normalized === 'VIMEO') return 'Vimeo';

  return materialType || '';
}

function getCategorySelectionsFromMaterial(material) {
  if (Array.isArray(material?.categories) && material.categories.length > 0) {
    return material.categories.map((category) => ({
      category: category.categoryId || '',
      subcategory: category.subcategory || ''
    }));
  }

  return [{ category: material?.category || '', subcategory: material?.subcategory || '' }];
}

/** ========= Página ========= **/
function GerenciarMateriais() {
  const navigate = useNavigate();
  const isNaturezaComSemana = (value) =>
    value === 'Gabarito' || value === 'Resumo' || value === 'Conteúdo';
  const isNaturezaSemanaObrigatoria = (value) =>
    value === 'Gabarito' || value === 'Resumo' || value === 'Conteúdo';

  // Campos do formulário
  const [title, setTitle] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [description, setDescription] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [naturezaMaterial, setNaturezaMaterial] = useState('');
  const [semana, setSemana] = useState('');
  const [materialType, setMaterialType] = useState('');
  const [file, setFile] = useState(null);
  const [materialUrl, setMaterialUrl] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [allowDownload, setAllowDownload] = useState(false);

  // Sistema de múltiplas categorias (mantendo interface original)
  const [categorySelections, setCategorySelections] = useState([
    { category: '', subcategory: '' }
  ]);

  // Listas e estado
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [loading, setLoading] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [newMaterialId, setNewMaterialId] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // NOVO: dados do usuário no Firestore
  const [uploadProgress, setUploadProgress] = useState('');
  const [editingMaterial, setEditingMaterial] = useState(null);

  // Recorte manual (opcional)
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingThumbnailBlob, setPendingThumbnailBlob] = useState(null);
  const [pendingThumbPreviewUrl, setPendingThumbPreviewUrl] = useState('');
  const isEditing = !!editingMaterial;
  const adminContentRef = useRef(null);
  const formCardRef = useRef(null);
  const titleInputRef = useRef(null);

  // Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setCurrentUser(firebaseUser);
      if (firebaseUser) {
        // BUSCAR dados do usuário no Firestore para pegar o nome correto
        try {
          console.log('🔍 Buscando dados do usuário:', firebaseUser.email);

          // Buscar primeiro na coleção 'usuarios' (nova)
          const usuariosRef = collection(db, 'usuarios');
          const qUsuarios = query(usuariosRef, where('email', '==', firebaseUser.email));
          const usuariosSnapshot = await getDocs(qUsuarios);

          if (!usuariosSnapshot.empty) {
            const userData = usuariosSnapshot.docs[0].data();
            console.log('✅ Usuário encontrado na coleção usuarios:', userData);
            const resolvedUser = {
              uid: firebaseUser.uid,
              email: userData.email || firebaseUser.email,
              name: userData.name || userData.nome || firebaseUser.displayName || 'Usuário',
              plano: userData.plano || 'Free',
              role: userData.role || 'admin',
              source: 'usuarios'
            };
            setUserProfile(resolvedUser);
          } else {
            // Buscar na coleção 'admins' (antiga) como fallback
            const adminsRef = collection(db, 'admins');
            const qAdmins = query(adminsRef, where('email', '==', firebaseUser.email));
            const adminsSnapshot = await getDocs(qAdmins);

            if (!adminsSnapshot.empty) {
              const adminData = adminsSnapshot.docs[0].data();
              console.log('✅ Usuário encontrado na coleção admins:', adminData);
              const resolvedUser = {
                uid: firebaseUser.uid,
                name: adminData.nome || adminData.name || firebaseUser.displayName || 'Admin',
                email: adminData.email || firebaseUser.email,
                plano: 'Premium',
                role: 'admin',
                source: 'admins'
              };
              setUserProfile(resolvedUser);
            } else {
              // Fallback para dados do Firebase Auth
              console.log('⚠ Usuário não encontrado no Firestore, usando dados do Auth');
              const resolvedUser = {
                uid: firebaseUser.uid,
                name: firebaseUser.displayName || 'Usuário',
                email: firebaseUser.email,
                plano: 'Free',
                role: 'admin',
                source: 'auth'
              };
              setUserProfile(resolvedUser);
            }
          }
        } catch (error) {
          console.error('❌ Erro ao buscar dados do usuário:', error);
          const resolvedUser = {
            uid: firebaseUser.uid,
            name: firebaseUser.displayName || 'Usuário',
            email: firebaseUser.email,
            plano: 'Free',
            role: 'admin',
            source: 'auth'
          };
          setUserProfile(resolvedUser);
        }
      } else {
        navigate('/admin-login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Carrega categorias/materiais
  useEffect(() => {
    fetchCategories();
    fetchMaterials();
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!editingMaterial) return;

    requestAnimationFrame(() => {
      adminContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      titleInputRef.current?.focus();
    });
  }, [editingMaterial]);

  const fetchCategories = async () => {
    const snap = await getDocs(collection(db, 'categories'));
    setCategories(
      snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name,
          subcategories: Array.isArray(data.subcategories) ? data.subcategories.map((s) => s.name) : [],
        };
      })
    );
  };

  const fetchMaterials = async () => {
    const snap = await getDocs(collection(db, 'materials'));
    const list = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    setMaterials(list);
    setFilteredMaterials(list);
  };

  const getSubcategoryOptions = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    return cat ? cat.subcategories : [];
  };

  // Funções para gerenciar múltiplas categorias
  const addCategorySelection = () => {
    setCategorySelections([...categorySelections, { category: '', subcategory: '' }]);
  };

  const removeCategorySelection = (index) => {
    if (categorySelections.length > 1) {
      const newSelections = categorySelections.filter((_, i) => i !== index);
      setCategorySelections(newSelections);
    }
  };

  const updateCategorySelection = (index, field, value) => {
    const newSelections = [...categorySelections];
    newSelections[index] = {
      ...newSelections[index],
      [field]: value,
      // Reset subcategory quando muda categoria
      ...(field === 'category' && { subcategory: '' })
    };
    setCategorySelections(newSelections);
  };

  const clearPendingThumb = () => {
    setPendingThumbnailBlob(null);
    if (pendingThumbPreviewUrl) URL.revokeObjectURL(pendingThumbPreviewUrl);
    setPendingThumbPreviewUrl('');
  };

  const handleFileChange = async (e) => {
    const f = e.target.files[0] || null;
    setFile(f);
    clearPendingThumb();

    // Abre modal para PDF imediatamente
    if (f && (materialType === 'PDF' || /\.pdf$/i.test(f.name))) {
      setShowCropModal(true);
    }
  };

  // Fallback auto (caso a pessoa pule o recorte)
  const generatePdfThumbnailAuto = async (pdfUrl) => {
    setUploadProgress('Gerando preview automático do PDF...');
    await ensurePdfJsLoaded();

    const base = document.createElement('canvas');
    await renderPdfFirstPageToCanvas({ source: pdfUrl, targetCanvas: base, maxWidth: 1400 });

    // 16:9 central
    const w = Math.floor(base.width * 0.9);
    const h = Math.floor((w * 9) / 16);
    const x = Math.floor((base.width - w) / 2);
    const y = Math.floor((base.height - h) / 3);

    const out = document.createElement('canvas');
    out.width = 800;
    out.height = 450;
    const ctx = out.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(base, x, y, w, h, 0, 0, out.width, out.height);
    return await blobFromCanvas(out, 'image/jpeg', 0.9);
  };


  const formatDateTime = (isoString) => {
    const d = new Date(isoString);
    return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const resetForm = () => {
    setTitle('');
    setMetaTitle('');
    setDescription('');
    setMetaDescription('');
    setKeywords('');
    setNaturezaMaterial('');
    setSemana('');
    setCategorySelections([{ category: '', subcategory: '' }]);
    setMaterialType('');
    setFile(null);
    setMaterialUrl('');
    setCoverImage('');
    setAllowDownload(false);
    setMensagem('');
    setUploadProgress('');
    setEditingMaterial(null);
    clearPendingThumb();
  };

  const handleMaterialTypeChange = (nextType) => {
    if (materialType && materialType !== nextType) {
      setFile(null);
      setMaterialUrl('');
      setCoverImage('');
      clearPendingThumb();
    }

    setMaterialType(nextType);
    if (nextType !== 'PDF') {
      setAllowDownload(false);
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setTitle(material.title || '');
    setMetaTitle(material.metaTitle || '');
    setDescription(material.description || '');
    setMetaDescription(material.metaDescription || '');
    setKeywords(Array.isArray(material.keywords) ? material.keywords.join(', ') : '');
    setNaturezaMaterial(material.naturezaMaterial || '');
    setSemana(material.semana || '');
    setMaterialType(getFormMaterialType(material.materialType));
    setFile(null);
    setMaterialUrl(material.materialUrl || '');
    setCoverImage(material.coverImage || '');
    setAllowDownload(material.allowDownload === true);
    setCategorySelections(getCategorySelectionsFromMaterial(material));
    setMensagem('');
    clearPendingThumb();
  };

  const cancelEditing = () => {
    resetForm();
    setMensagem('Edição cancelada.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensagem('');
    setLoading(true);
    setUploadProgress('Iniciando upload...');

    try {
      if (!currentUser) throw new Error('Usuário não autenticado');
      if (!userProfile) throw new Error('Dados do usuário não carregados');

      // Validar se pelo menos uma categoria está selecionada
      const validSelections = categorySelections.filter(sel => sel.category);
      if (validSelections.length === 0) throw new Error('Selecione pelo menos uma categoria');
      if (!naturezaMaterial) throw new Error('Selecione a natureza do material');
      if (isNaturezaSemanaObrigatoria(naturezaMaterial) && !semana) throw new Error('Selecione a semana');

      const currentEditFormType = getFormMaterialType(editingMaterial?.materialType);
      const isEditingFileWithoutReplacement = isEditing
        && isFileMaterialType(materialType)
        && !file;

      if (isEditingFileWithoutReplacement && currentEditFormType !== materialType) {
        throw new Error('Selecione um novo arquivo para alterar o tipo do material.');
      }

      let finalUrl = materialUrl;
      let finalType = materialType;
      let finalCoverImage = coverImage;
      const timestamp = Date.now();

      // Upload de arquivo (PDF/PNG/JPG)
      if (file && (materialType === 'PDF' || materialType === 'PNG' || materialType === 'JPG' || materialType === 'Documento' || materialType === 'Slide')) {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        finalType = ext.toUpperCase();

        setUploadProgress('Fazendo upload do arquivo...');
        const storageRef = ref(storage, `materials/${timestamp}_${file.name}`);

        const meta =
          ext === 'pdf'
            ? { contentType: 'application/pdf' }
            : ext === 'png'
              ? { contentType: 'image/png' }
              : ext === 'jpg' || ext === 'jpeg'
                ? { contentType: 'image/jpeg' }
                : ext === 'docx'
                  ? { contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }
                  : ext === 'doc'
                    ? { contentType: 'application/msword' }
                    : ext === 'pptx'
                      ? { contentType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' }
                      : ext === 'ppt'
                        ? { contentType: 'application/vnd.ms-powerpoint' }
                        : { contentType: 'application/octet-stream' };

        const uploadSnap = await uploadBytes(storageRef, file, meta);
        finalUrl = await getDownloadURL(uploadSnap.ref);

        if (ext === 'pdf') {
          // Usa recorte manual (se houver) ou gera automático
          let thumbBlob = pendingThumbnailBlob;
          if (!thumbBlob) {
            thumbBlob = await generatePdfThumbnailAuto(finalUrl);
          }
          if (thumbBlob) {
            const thumbName = file.name.replace(/\.pdf$/i, '.jpg');
            const thumbnailRef = ref(storage, `thumbnails/${timestamp}_${thumbName}`);
            const thumbnailSnap = await uploadBytes(thumbnailRef, thumbBlob, { contentType: 'image/jpeg' });
            finalCoverImage = await getDownloadURL(thumbnailSnap.ref);
          } else {
            finalCoverImage = ''; // preview inline cuidará
          }
        } else if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') {
          finalCoverImage = finalUrl; // PNG/JPG usam a própria
        } else if (ext === 'docx' || ext === 'doc' || ext === 'pptx' || ext === 'ppt') {
          // Sem preview real para DOC/PPT: usar ícone genérico no card
          finalCoverImage = '';
        } else {
          finalCoverImage = '';
        }
      }

      if (isEditingFileWithoutReplacement) {
        finalUrl = editingMaterial.materialUrl || '';
        finalType = (editingMaterial.materialType || materialType || '').toUpperCase();
        finalCoverImage = editingMaterial.coverImage || '';
      }

      // Vídeos
      if (materialType === 'YouTube') {
        const videoId = materialUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\n?#]+)/)?.[1];
        if (videoId) finalCoverImage = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      }
      if (materialType === 'Vimeo') {
        const vimeoId = materialUrl.match(/vimeo\.com\/(\d+)/)?.[1];
        if (vimeoId) finalCoverImage = `https://i.vimeocdn.com/video/${vimeoId}_640x360.jpg`;
      }

      setUploadProgress('Salvando no banco de dados...');

      // Preparar dados das categorias
      const categoriesData = validSelections.map(sel => {
        const cat = categories.find(c => c.id === sel.category);
        return {
          categoryId: sel.category,
          categoryName: cat ? cat.name : '',
          subcategory: sel.subcategory || null
        };
      });

      // Para compatibilidade, usar primeira categoria como principal
      const primaryCategory = validSelections[0];

      console.log('📝 Salvando material com autor:', userProfile.name, '(', userProfile.email, ')');

      const successMessage = isEditing
        ? 'Material atualizado com sucesso!'
        : `Material cadastrado com sucesso por ${userProfile.name}!`;

      const resolvedMaterialType = (finalType || materialType || '').toUpperCase();
      const allowMaterialDownload = resolvedMaterialType === 'PDF' ? allowDownload : false;

      const materialData = {
        title,
        metaTitle,
        description,
        metaDescription,
        keywords: keywords.split(',').map((k) => k.trim()).filter((k) => k),
        naturezaMaterial,
        semana: isNaturezaComSemana(naturezaMaterial) ? semana : '',

        // Nova estrutura para múltiplas categorias
        categories: categoriesData,

        // Mantém campos antigos para compatibilidade
        category: primaryCategory.category,
        subcategory: primaryCategory.subcategory || '',

        materialType: resolvedMaterialType,
        materialUrl: finalUrl,
        coverImage: finalCoverImage || '',
        hasThumbnail: resolvedMaterialType === 'PDF' && !!finalCoverImage,
        allowDownload: allowMaterialDownload,
        createdAt: isEditing ? (editingMaterial.createdAt || new Date().toISOString()) : new Date().toISOString(),
        updatedAt: new Date().toISOString(),

        // CORRIGIDO: Usar dados do userProfile em vez de currentUser
        authorEmail: editingMaterial?.authorEmail || userProfile.email,
        authorName: editingMaterial?.authorName || userProfile.name,
        authorRole: editingMaterial?.authorRole || userProfile.role,
        authorSource: editingMaterial?.authorSource || userProfile.source,
        lastEditedByEmail: userProfile.email,
        lastEditedByName: userProfile.name,
        lastEditedByRole: userProfile.role
      };

      console.log('✅ Material data preparado:', {
        authorName: materialData.authorName,
        authorEmail: materialData.authorEmail,
        authorRole: materialData.authorRole
      });

      if (isEditing) {
        await updateDoc(doc(db, 'materials', editingMaterial.id), materialData);
      } else {
        const docRef = await addDoc(collection(db, 'materials'), materialData);
        setNewMaterialId(docRef.id);
        setIsSuccessModalOpen(true);
      }

      await fetchMaterials();
      resetForm();
      setMensagem(successMessage);
    } catch (err) {
      console.error('❌ Erro ao salvar material:', err);
      setMensagem('Erro: ' + err.message);
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este material?')) return;
    try {
      await deleteDoc(doc(db, 'materials', id));
      if (editingMaterial?.id === id) {
        resetForm();
      }
      setMensagem('Material deletado com sucesso.');
      await fetchMaterials();
    } catch (error) {
      setMensagem('Erro ao deletar material: ' + error.message);
    }
  };

  const handleSearch = (e) => {
    const inputValue = e.target.value;
    const q = normalizeText(inputValue);
    setSearchQuery(inputValue.toLowerCase());
    setFilteredMaterials(
      materials.filter(
        (m) =>
          normalizeText(m.title).includes(q) ||
          normalizeText(m.description).includes(q) ||
          normalizeText(m.subcategory).includes(q) ||
          (m.categories && m.categories.some(cat =>
            normalizeText(cat.categoryName).includes(q) ||
            normalizeText(cat.subcategory).includes(q)
          ))
      )
    );
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Preview inline para PDFs sem coverImage salvo
  function PdfInlineThumb({ pdfUrl }) {
    const [thumbUrl, setThumbUrl] = useState('');
    const revokeRef = useRef(null);

    useEffect(() => {
      let active = true;
      (async () => {
        try {
          await ensurePdfJsLoaded();
          const base = document.createElement('canvas');
          await renderPdfFirstPageToCanvas({ source: pdfUrl, targetCanvas: base, maxWidth: 1000 });

          // 16:9 central
          const w = Math.floor(base.width * 0.9);
          const h = Math.floor((w * 9) / 16);
          const x = Math.floor((base.width - w) / 2);
          const y = Math.floor((base.height - h) / 3);

          const out = document.createElement('canvas');
          out.width = 480;
          out.height = 270;
          const ctx = out.getContext('2d');
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, out.width, out.height);
          ctx.drawImage(base, x, y, w, h, 0, 0, out.width, out.height);

          const blob = await blobFromCanvas(out, 'image/jpeg', 0.85);
          if (!active) return;
          const url = URL.createObjectURL(blob);
          revokeRef.current = url;
          setThumbUrl(url);
        } catch {
          setThumbUrl('');
        }
      })();
      return () => {
        active = false;
        if (revokeRef.current) URL.revokeObjectURL(revokeRef.current);
      };
    }, [pdfUrl]);

    if (!thumbUrl) {
      return (
        <div className="material-cover-placeholder">
          <FaFilePdf />
        </div>
      );
    }

    return (
      <img
        src={thumbUrl}
        alt="Preview"
        className="material-cover-image"
        style={{ objectFit: 'cover', background: '#f8f9fa' }}
        onError={(e) => {
          e.target.src = 'https://via.placeholder.com/100x60/e9ecef/6c757d?text=Preview';
        }}
      />
    );
  }

  const renderMaterialThumbnail = (material) => {
    const mt = (material.materialType || '').toUpperCase();
    const cover = material.coverImage || '';
    const url = material.materialUrl || '';

    if (cover && !cover.toLowerCase().endsWith('.pdf')) {
      return (
        <img
          src={cover}
          alt="Preview"
          className="material-cover-image"
          style={{ objectFit: 'cover', background: '#f8f9fa' }}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/100x60/e9ecef/6c757d?text=Preview';
          }}
        />
      );
    }

    if (mt === 'PDF' && url) {
      return <PdfInlineThumb pdfUrl={url} />;
    }

    return (
      <div className="material-cover-placeholder">
        {mt === 'PDF' && <FaFilePdf />}
        {(mt === 'PNG' || mt === 'JPG' || mt === 'JPEG') && <FaImage />}
        {(mt === 'YOUTUBE' || mt === 'VIMEO') && <FaEye />}
      </div>
    );
  };

  // Função para exibir as categorias do material na lista
  const renderMaterialCategories = (material) => {
    if (material.categories && material.categories.length > 0) {
      return material.categories.map((cat, index) => (
        <span key={index} style={{
          fontSize: '0.75rem',
          background: '#e3f2fd',
          color: '#1565c0',
          padding: '0.15rem 0.4rem',
          borderRadius: '12px',
          marginRight: '0.25rem',
          display: 'inline-block',
          marginBottom: '0.15rem'
        }}>
          {cat.subcategory || cat.categoryName}
        </span>
      ));
    }

    // Fallback para materiais antigos
    return (
      <span style={{
        fontSize: '0.75rem',
        background: '#e3f2fd',
        color: '#1565c0',
        padding: '0.15rem 0.4rem',
        borderRadius: '12px'
      }}>
        {material.subcategory || 'Sem categoria'}
      </span>
    );
  };

  // Mostrar loading se dados do usuário ainda não carregaram
  if (!userProfile) {
    return (
      <div className="gerenciar-materiais admin-panel-container">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTopColor: '#209cd3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p>Carregando dados do usuário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gerenciar-materiais admin-panel-container">
      {/* Modal de sucesso */}
      {isSuccessModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setIsSuccessModalOpen(false)}>
              <FaTimes />
            </button>
            <h2>Material cadastrado com sucesso!</h2>
            <div className="modal-actions">
              <button onClick={() => navigate(`/conteudo/${newMaterialId}`)} className="ver-conteudo">
                <FaEye /> Ver Conteúdo
              </button>
              <button onClick={() => setIsSuccessModalOpen(false)} className="fechar-button">
                <FaTimes /> Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de recorte do PDF */}
      {showCropModal && file && (
        <PdfCropperModal
          file={file}
          onConfirm={(blob) => {
            if (pendingThumbPreviewUrl) URL.revokeObjectURL(pendingThumbPreviewUrl);
            const url = URL.createObjectURL(blob);
            setPendingThumbnailBlob(blob);
            setPendingThumbPreviewUrl(url);
            setShowCropModal(false);
          }}
          onCancel={() => {
            setShowCropModal(false);
          }}
        />
      )}

      <header className="admin-header">
        <button className="back-button" onClick={() => navigate('/admin-panel')}>
          <FaArrowLeft /> Voltar
        </button>
        <div className="header-title">
          <h1>Gerenciar Materiais</h1>
          <p>Logado como: <strong>{userProfile.name}</strong> ({userProfile.role})</p>
        </div>
      </header>

      <div className="admin-content" ref={adminContentRef}>
        <div className="admin-form-card" ref={formCardRef}>
          <h2>{isEditing ? 'Editar Material' : 'Adicionar Novo Material'}</h2>
          {isEditing && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.9rem 1rem',
                borderRadius: '10px',
                background: '#fff8e1',
                border: '1px solid #ffe082',
                color: '#8a6d3b'
              }}
            >
              Editando: <strong>{editingMaterial?.title}</strong>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Título</label>
              <input
                ref={titleInputRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Meta Title</label>
              <input type="text" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} required />
            </div>

            <div className="form-group">
              <label>Descrição</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} required />
            </div>

            <div className="form-group">
              <label>Meta Description</label>
              <textarea value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} required />
            </div>

            <div className="form-group">
              <label>Palavras-chave</label>
              <input
                type="text"
                placeholder="separadas por vírgula"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Natureza do Material</label>
              <select
                value={naturezaMaterial}
                onChange={(e) => {
                  const value = e.target.value;
                  setNaturezaMaterial(value);
                  if (!isNaturezaComSemana(value)) setSemana('');
                }}
                required
              >
                <option value="">Selecione o tipo</option>
                <option value="Gabarito">Gabarito</option>
                <option value="Prova">Prova</option>
                <option value="Resumo">Resumo</option>
                <option value="Outros">Outros</option>
              </select>
            </div>

            {isNaturezaComSemana(naturezaMaterial) && (
              <div className="form-group">
                <label>Semana</label>
                <select
                  value={semana}
                  onChange={(e) => setSemana(e.target.value)}
                  required={isNaturezaSemanaObrigatoria(naturezaMaterial)}
                >
                  <option value="">Selecione a semana</option>
                  <option value="Semana 1">Semana 1</option>
                  <option value="Semana 2">Semana 2</option>
                  <option value="Semana 3">Semana 3</option>
                  <option value="Semana 4">Semana 4</option>
                  <option value="Semana 5">Semana 5</option>
                  <option value="Semana 6">Semana 6</option>
                  <option value="Semana 7">Semana 7</option>
                  <option value="Semana 8">Semana 8</option>
                </select>
              </div>
            )}

            {/* Sistema de múltiplas categorias mantendo interface original */}
            <div className="form-group">
              <label>Categorias e Subcategorias</label>
              {categorySelections.map((selection, index) => (
                <div key={index} style={{
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'center',
                  marginBottom: '0.75rem',
                  padding: '0.75rem',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{ flex: 1 }}>
                    <select
                      value={selection.category}
                      onChange={(e) => updateCategorySelection(index, 'category', e.target.value)}
                      required={index === 0}
                      style={{ marginBottom: '0.5rem' }}
                    >
                      <option value="">Selecione uma categoria</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selection.category && (
                    <div style={{ flex: 1 }}>
                      <select
                        value={selection.subcategory}
                        onChange={(e) => updateCategorySelection(index, 'subcategory', e.target.value)}
                      >
                        <option value="">Selecione uma subcategoria</option>
                        {getSubcategoryOptions(selection.category).map((sub) => (
                          <option key={sub} value={sub}>
                            {sub}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {categorySelections.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCategorySelection(index)}
                      style={{
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Remover categoria"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addCategorySelection}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#2ecc71',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#27ae60'}
                onMouseOut={(e) => e.target.style.background = '#2ecc71'}
              >
                <FaPlus /> Adicionar mais categorias
              </button>
              <small style={{ color: '#6c757d', marginTop: '0.5rem', display: 'block' }}>
                Você pode adicionar múltiplas categorias para melhor organização do conteúdo.
              </small>
            </div>

            <div className="form-group">
              <label>Tipo de Material</label>
              <select value={materialType} onChange={(e) => handleMaterialTypeChange(e.target.value)} required>
                <option value="">Selecione o tipo</option>
                <option value="PDF">PDF (com thumbnail automático + recorte manual)</option>
                <option value="PNG">Imagem PNG</option>
                <option value="Documento">Documento (docx)</option>
                <option value="Slide">Slide (pptx)</option>
                <option value="JPG">Imagem JPG</option>
                <option value="YouTube">Vídeo do YouTube</option>
                <option value="Vimeo">Vídeo do Vimeo</option>
              </select>
            </div>

            {(materialType === 'YouTube' || materialType === 'Vimeo') && (
              <div className="form-group">
                <label>URL do Vídeo</label>
                <input
                  type="url"
                  value={materialUrl}
                  onChange={(e) => setMaterialUrl(e.target.value)}
                  placeholder={`Cole aqui a URL do ${materialType}`}
                  required
                />
              </div>
            )}

            {(materialType === 'PDF' || materialType === 'PNG' || materialType === 'JPG' || materialType === 'Documento' || materialType === 'Slide') && (
              <div className="form-group">
                <label>Arquivo ({materialType})</label>
                <input
                  type="file"
                  accept={
                    materialType === 'PDF'
                      ? '.pdf,application/pdf'
                      : materialType === 'PNG'
                        ? '.png,image/png'
                        : materialType === 'JPG'
                          ? '.jpg,.jpeg,image/jpeg'
                          : materialType === 'Documento'
                            ? '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                            : '.ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation'
                  }
                  onChange={handleFileChange}
                  required={!isEditing}
                />
                {isEditing && (
                  <small style={{ color: '#6c757d', marginTop: '0.5rem', display: 'block' }}>
                    Deixe sem selecionar para manter o arquivo atual.
                  </small>
                )}
                {materialType === 'PDF' && (
                  <small style={{ color: '#6c757d', marginTop: '0.5rem', display: 'block' }}>
                    Você pode ajustar o recorte da 1ª página antes de salvar (abre uma janela de recorte).
                  </small>
                )}
              </div>
            )}

            {isEditing && isFileMaterialType(materialType) && editingMaterial?.materialUrl && !file && (
              <div
                style={{
                  marginTop: '-0.75rem',
                  padding: '0.75rem',
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  color: '#495057',
                  fontSize: '0.9rem'
                }}
              >
                O arquivo atual será mantido até que você envie um novo.
              </div>
            )}

            {materialType === 'PDF' && pendingThumbPreviewUrl && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '0.25rem' }}>Pré-visualização do recorte:</div>
                <img
                  src={pendingThumbPreviewUrl}
                  alt="Preview recortado"
                  style={{ width: 320, height: 180, objectFit: 'cover', borderRadius: 8, border: '1px solid #e9ecef' }}
                />
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button type="button" className="ver-conteudo" onClick={() => setShowCropModal(true)}>
                    Refazer recorte
                  </button>
                  <button
                    type="button"
                    className="fechar-button"
                    onClick={() => {
                      clearPendingThumb();
                      setShowCropModal(true);
                    }}
                  >
                    Remover recorte
                  </button>
                </div>
              </div>
            )}

            {materialType === 'PDF' && (
              <div className="form-group checkbox-group">
                <label className="checkbox-inline">
                  <input
                    type="checkbox"
                    checked={allowDownload}
                    onChange={(e) => setAllowDownload(e.target.checked)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-copy">
                    <strong>Permitir download deste PDF</strong>
                    <small>
                      Quando marcado, o botão de download aparecerá na tela de conteúdo para usuários com acesso ao PDF.
                    </small>
                  </span>
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button type="submit" disabled={loading}>
                {isEditing ? <FaSave /> : <FaPlus />}
                {loading ? 'Processando...' : isEditing ? 'Salvar Alterações' : 'Enviar Material'}
              </button>
              {isEditing && (
                <button
                  type="button"
                  className="secondary-action"
                  onClick={cancelEditing}
                  disabled={loading}
                >
                  <FaTimes /> Cancelar Edição
                </button>
              )}
            </div>

            {uploadProgress && (
              <div
                className="upload-progress"
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: '#e3f2fd',
                  border: '1px solid #bbdefb',
                  borderRadius: '8px',
                  color: '#1565c0',
                  fontSize: '0.9rem',
                }}
              >
                {uploadProgress}
              </div>
            )}
          </form>

          {mensagem && <div className={`mensagem ${mensagem.includes('Erro') ? 'erro' : 'sucesso'}`}>{mensagem}</div>}
        </div>

        <div className="admin-list">
          <h2>Lista de Materiais ({filteredMaterials.length})</h2>
          <div className="search-bar">
            <FaSearch />
            <input
              placeholder="Buscar por título, descrição ou categoria..."
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>

          <div className="admin-table">
            <div className="table-header">
              <span>Preview</span>
              <span>Título</span>
              <span>Autor</span>
              <span>Categorias</span>
              <span>Publicado em</span>
              <span>Ações</span>
            </div>

            {filteredMaterials.map((mat) => (
              <div key={mat.id} className="table-row">
                <span>{renderMaterialThumbnail(mat)}</span>
                <span>
                  <strong>{mat.title}</strong>
                  <br />
                  <small style={{ color: '#6c757d' }}>
                    {(mat.materialType || '').toUpperCase()}
                    {mat.hasThumbnail && <span style={{ color: '#28a745' }}> • ✓ Thumbnail</span>}
                  </small>
                </span>
                <span>
                  <strong>{mat.authorName || 'Autor não informado'}</strong>
                  <br />
                  <small style={{ color: '#6c757d' }}>
                    {mat.authorRole === 'especialista' ? 'Especialista' : 'Admin'}
                  </small>
                </span>
                <span>
                  {renderMaterialCategories(mat)}
                </span>
                <span>{formatDateTime(mat.createdAt)}</span>
                <span className="actions">
                  <button onClick={() => navigate(`/conteudo/${mat.id}`)} className="ver-conteudo" title="Visualizar conteúdo">
                    <FaEye /> Ver
                  </button>
                  <button onClick={() => handleEdit(mat)} className="editar" title="Editar material">
                    <FaEdit /> Editar
                  </button>
                  <button onClick={() => handleDelete(mat.id)} className="deletar" title="Deletar material">
                    <FaTrash /> Apagar
                  </button>
                </span>
              </div>
            ))}

            {filteredMaterials.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#6c757d',
                }}
              >
                {searchQuery ? 'Nenhum material encontrado para a busca.' : 'Nenhum material cadastrado ainda.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {showBackToTop && (
        <button className="back-to-top" onClick={scrollToTop}>
          <FaArrowUp /> Topo
        </button>
      )}
    </div>
  );
}

export default GerenciarMateriais;
